import type { ApolloClient } from '@apollo/client';
import type { GetUrlMetadataQuery, GetUrlMetadataQueryVariables } from '@/graphql/query/GetUrlMetadata.query';
import type { UrlMetadata } from '@/stores/pinia/useRoutingStore';

import { GetUrlMetadataDocument } from '@/graphql/query/GetUrlMetadata.query';

import { OnboardingStatus } from '@/stores/useUserStoreTypes';
import { Domain } from '@/routing/helpers/subdomain-helper';
import { ObjectType } from '@/@types/graphql-types';

import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useTrackingStore } from '@/stores/pinia/useTrackingStore';

// prettier-ignore
const GLOBAL_ROUTE_NAMES = [
	'app.home',
	// handled with URL Metadata query from BE
	// 'app.sports.subdomain.landing',
	// 'app.editorial.subdomain.landing',
]

const REFRESH_TOKEN_ROUTE_NAME = 'app.refreshtoken';
const sportRoutePrefixes = ['app.sports', 'app.home'];

export default defineNuxtRouteMiddleware(async (to, from) => {
	if (to.name === REFRESH_TOKEN_ROUTE_NAME || from.name === REFRESH_TOKEN_ROUTE_NAME) return;
	// Skip non-app navigations (assets, source maps, etc.)
	if (to.path.includes('.')) return;

	const nuxtApp = useNuxtApp();
	const routingStore = useRoutingStore();
	const trackingStore = useTrackingStore();
	const languageStore = useLanguageStore();

	trackingStore.increasePagesVisited();

	/* Route Critical Data */
	const localesPromise = languageStore.fetchLocales();

	const isSportsOrHome = sportRoutePrefixes.some(prefix => to.name?.toString().startsWith(prefix));
	const sportsTranslationPromise = isSportsOrHome ? languageStore.fetchSportsTranslation() : undefined;

	/* Fetch and Update URL Metadata */
	const isGlobalRoute = !to.name || GLOBAL_ROUTE_NAMES.includes(to.name.toString());
	const urlMetadataPromise = isGlobalRoute
		? fetchMockMetadata(useUserStore().onboardingStatus, languageStore.locale, routingStore.activeDomain)
		: fetchUrlMetadata(to.path, routingStore.activeDomain, nuxtApp.$apolloClients.default ?? null);

	async function updateRoutingStore(urlMetadataPromise: Promise<UrlMetadata | null>) {
		const urlMetadata = await urlMetadataPromise;

		// Maintain Nuxt's context because this runs after an unawaited
		// async (in client) and the context gets lost
		return nuxtApp.runWithContext(() => {
			routingStore.setUrlMetadata(urlMetadata);

			const event = useRequestEvent();
			if (event) {
				event.context.activeRouteName = to?.name ?? null;
				useResponseHeader('x-page-type').value = routingStore.pageType;
			}
		});
	}

	const updatePromise = updateRoutingStore(urlMetadataPromise);

	// Await after kicking off URLMetadata promise
	await Promise.all([localesPromise, sportsTranslationPromise]);

	// on the server, we must await the url metadata.
	// but on the client, we don't care if the url metadata is coming in a bit later.
	if (import.meta.server) await updatePromise;
});

async function fetchUrlMetadata(fullPath: string, site: Domain, apolloClient: ApolloClient | null = null) {
	if (apolloClient == null) return null;

	try {
		const urlMetadata = await apolloClient.query<GetUrlMetadataQuery, GetUrlMetadataQueryVariables>({
			query: GetUrlMetadataDocument,
			variables: { fullPath, site },
		});

		return urlMetadata.data?.urlV2 ?? null;
	}
	catch {
		return null;
	}
}

function fetchMockMetadata(onboardingStatus: OnboardingStatus | null, locale: string, site: Domain) {
	const technicalName =
		site === Domain.EDITORIAL
			? 'VIEW_HOME_GUIDES'
			: onboardingStatus === OnboardingStatus.GLOBAL
				? 'VIEW_HOME_GLOBAL'
				: 'VIEW_HOME';

	return Promise.resolve({
		id: 'home',
		urlID: 0,
		locale,
		site,
		objectType: ObjectType.Page,
		fullPath: '/',
		metaTitle: 'JustWatch - The Streaming Guide',
		metaDescription: 'All your streaming services in one app.',
		metaKeywords: '',
		page: { id: 'home', technicalName },
		heading1: null,
		heading2: null,
		metaRobots: '',
		hrefLangTags: [],
		allHrefLangTags: [],
	} as GetUrlMetadataQuery['urlV2']);
}
